/**
 * Settings > Gates (SAM-946, mock Gates v3 approved 15 Oct 2026): the tenant's external and internal gates.
 * Pure helpers; the pages only render. Project gates and the estate group variant are Sprint 7.
 */
import type { Gate, GateType, InvitationType } from '../../../api/generated';
import { ZONES_ENABLED } from '../zones/zonesModel';

/**
 * The zone of an internal gate comes from the tenant's zone catalog (Settings > Zones). Until the catalog exists the
 * field and the column stay hidden and `zone_id` is never sent.
 */
export const GATE_ZONE_ENABLED = ZONES_ENABLED;

export type GateKind = 'external' | 'internal';
export type GateTab = 'all' | GateKind;

export interface GateRow {
  id: number;
  name: string;
  code: string;
  typeId: number | undefined;
  typeName: string;
  kind: GateKind | null;
  zoneId: number | null;
}

/**
 * The kind comes from the gate type's name prefix: «External Main», «External Service», «External M & S» and the
 * three «Internal …» of the tenant (Logic, 15 Oct 2026). `gate_type.kind` arrives with the zones migration; until
 * then the prefix is the rule, and a bare «External» / «Internal» still works.
 */
export const gateKind = (typeName?: string | null): GateKind | null => {
  const m = /^\s*(external|internal)\b/i.exec(typeName ?? '');
  return m ? (m[1].toLowerCase() as GateKind) : null;
};

export interface GateTypeOption {
  id: number;
  name: string;
  kind: GateKind;
}

/** The real gate types the form offers, in the order the backend lists them; a type with no kind is not offered. */
export const gateTypeOptions = (types: GateType[]): GateTypeOption[] =>
  types.flatMap((t) => {
    const kind = gateKind(t.name);
    return typeof t.id === 'number' && kind ? [{ id: t.id, name: t.name ?? '', kind }] : [];
  });

export const toGateRows = (gates: Gate[], types: GateType[]): GateRow[] => {
  const typeName = new Map(types.map((t) => [t.id, t.name ?? '']));
  return gates
    .filter((g): g is Gate & { id: number } => typeof g.id === 'number')
    .map((g) => {
      const name = typeName.get(g.gate_type_id) ?? '';
      return {
        id: g.id,
        name: g.name ?? '',
        code: g.code ?? '',
        typeId: g.gate_type_id,
        typeName: name,
        kind: gateKind(name),
        zoneId: typeof g.zone_id === 'number' ? g.zone_id : null,
      };
    });
};

export const filterGates = (rows: GateRow[], tab: GateTab, search: string): GateRow[] => {
  const q = search.trim().toLowerCase();
  return rows.filter(
    (r) => (tab === 'all' || r.kind === tab) && (!q || r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q)),
  );
};

export const gateCounts = (rows: GateRow[]) => ({
  all: rows.length,
  external: rows.filter((r) => r.kind === 'external').length,
  internal: rows.filter((r) => r.kind === 'internal').length,
});

/** Letters and numbers only, upper case: what the code field accepts as it is typed. */
export const normalizeCode = (value: string): string => value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

export interface GateForm {
  name: string;
  code: string;
  /** The id of the real gate type chosen (External Main, Internal Service, …). */
  typeId: number | null;
  /** Internal gates only: the zone of the catalog it controls (optional). */
  zoneId: number | null;
}

export const EMPTY_GATE_FORM: GateForm = { name: '', code: '', typeId: null, zoneId: null };

export type GateIssues = Partial<Record<keyof GateForm, string>>;

/** New gate: name and code are required and unique in the tenant. Edit: they are locked, only the type can change. */
export const gateIssues = (form: GateForm, rows: GateRow[], editingId?: number): GateIssues => {
  const issues: GateIssues = {};
  if (editingId === undefined) {
    const name = form.name.trim();
    if (!name) issues.name = 'Enter a name.';
    else if (rows.some((r) => r.name.trim().toLowerCase() === name.toLowerCase())) issues.name = 'A gate with this name already exists.';
    if (!form.code) issues.code = 'Enter a code.';
    else if (rows.some((r) => r.code.toLowerCase() === form.code.toLowerCase())) issues.code = 'A gate with this code already exists.';
  }
  if (form.typeId === null) issues.typeId = 'Choose a type.';
  return issues;
};

/**
 * The create / edit payload. `zone_id` only goes out once the zone catalog exists: the internal gate's zone, and null
 * for an external one (crud refuses a zone on an external gate with 400).
 */
export const gatePayload = (form: GateForm, editing: boolean, kind: GateKind | null = null): Partial<Gate> => ({
  ...(editing ? {} : { name: form.name.trim(), code: form.code }),
  gate_type_id: form.typeId ?? undefined,
  ...(GATE_ZONE_ENABLED ? { zone_id: (kind === 'internal' ? form.zoneId : null) as number | undefined } : {}),
});

export interface GateSaveFailure {
  /** The field the server's answer is about, when it is one. */
  field?: 'code';
  message: string;
}

/**
 * What a refused save means. The server keeps name and code fixed after creation (400) and refuses a repeated code
 * whatever its case (409).
 */
export const gateSaveFailure = (err: unknown, editing: boolean): GateSaveFailure => {
  const response = (err as { response?: { status?: number; data?: { message?: string; error?: string } } })?.response;
  if (response?.status === 409) return { field: 'code', message: 'A gate with this code already exists.' };
  if (response?.status === 400) {
    return { message: 'The server refused the change: the name and code of a gate cannot be changed.' };
  }
  return { message: editing ? 'Could not save the gate. Try again.' : 'Could not create the gate. Try again.' };
};

/**
 * Invitation Types point at a gate by its NAME in `external_gate_rules` / `internal_gate_rules` (Logic). A rule can
 * hold one name or several separated by commas or semicolons.
 */
export const invitationTypesUsing = (gateName: string, invitationTypes: InvitationType[]): string[] => {
  const target = gateName.trim().toLowerCase();
  if (!target) return [];
  const holds = (rules?: string | null) =>
    (rules ?? '').split(/[;,]/).some((r) => r.trim().toLowerCase() === target);
  return invitationTypes
    .filter((t) => holds(t.external_gate_rules) || holds(t.internal_gate_rules))
    .map((t) => t.name ?? `#${t.id}`);
};

export interface GateRuleOption {
  id: string;
  label: string;
}

/**
 * «All gates». The rules store `All` (Logic; the gatekeeper uses it that way), so that is what choosing it writes,
 * and a stored `all` / `ALL` in any case is this same option, left exactly as stored until the person changes it.
 */
export const ALL_GATES_OPTION: GateRuleOption = { id: 'All', label: 'All gates' };

const isAllValue = (value: string): boolean => value.trim().toLowerCase() === ALL_GATES_OPTION.id.toLowerCase();

/**
 * What the Gate Rules selector of an Invitation Type offers: All gates, plus the tenant's real gates of that kind
 * (External rules point at external gates, Internal at internal ones). The value stored is the gate NAME. A stored
 * value that is not one of them stays visible: a list of known gate names (the backend splits the rule on commas)
 * as it is, anything else, such as an old placeholder id, as a legacy value, so nothing is blanked silently.
 */
export const gateRuleOptions = (kind: GateKind, rows: GateRow[], current: string): GateRuleOption[] => {
  const options = [ALL_GATES_OPTION, ...rows.filter((r) => r.kind === kind).map((r) => ({ id: r.name, label: r.name }))];
  const value = current.trim();
  if (!value || isAllValue(value) || options.some((o) => o.id === value)) return options;
  const known = new Set(rows.map((r) => r.name.trim().toLowerCase()).concat(ALL_GATES_OPTION.id.toLowerCase()));
  const parts = value.split(',').map((p) => p.trim().toLowerCase());
  const isKnownList = parts.length > 1 && parts.every((p) => known.has(p));
  return [...options, { id: current, label: isKnownList ? value : `${value} (legacy value)` }];
};

/** The option a stored value is shown as: the same id, or «All gates» for `All` in any case. */
export const selectedGateRule = (options: GateRuleOption[], current: string): GateRuleOption[] => {
  if (!current) return [];
  if (isAllValue(current)) return [ALL_GATES_OPTION];
  return options.filter((o) => o.id === current);
};

export const usageText = (names: string[]): string => {
  if (names.length === 0) return '';
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Used in the gate rules of ${names.length} Invitation Type${names.length === 1 ? '' : 's'}: ${list}.`;
};
